import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

const BUY_SCRIPT_SRC = "https://js.stripe.com/v3/buy-button.js";

/** Mount the official Stripe Buy Button when buy_button_id + publishable_key are
 *  present. Falls back to the public Payment Link as a normal anchor so users
 *  can pay even if the script fails to load (e.g. ad-blockers).
 *
 *  The publishable key is intentionally read from CMS data (block.data) because
 *  this is a PUBLIC key — it is safe to expose, and reading from .env at runtime
 *  would not work for Owners who configure keys via Productieconfiguratie. */
export default function StripeBuyButtonBlock({ buyButtonId, publishableKey, paymentLinkUrl }) {
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (!buyButtonId || !publishableKey) return;
    const existing = document.querySelector(`script[src="${BUY_SCRIPT_SRC}"]`);
    if (existing) {
      setScriptReady(true);
      return;
    }
    const s = document.createElement("script");
    s.src = BUY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => setScriptReady(true);
    s.onerror = () => setScriptReady(false);
    document.body.appendChild(s);
  }, [buyButtonId, publishableKey]);

  const validPub = publishableKey && (publishableKey.startsWith("pk_live_") || publishableKey.startsWith("pk_test_"));
  const validId = buyButtonId && buyButtonId.startsWith("buy_btn_");

  if (validId && validPub) {
    return (
      <div data-testid="stripe-buy-button-wrapper" className="inline-block">
        {scriptReady &&
          React.createElement("stripe-buy-button", {
            "buy-button-id": buyButtonId,
            "publishable-key": publishableKey,
            "data-testid": "stripe-buy-button-embed",
          })}
        {!scriptReady && (
          <span className="text-xs text-gray-500" data-testid="stripe-buy-button-loading">Betaalknop laden…</span>
        )}
        {paymentLinkUrl && (
          <div className="mt-3 text-xs text-gray-500">
            Werkt de knop niet?{" "}
            <a
              href={paymentLinkUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-[#1a5193] hover:underline inline-flex items-center gap-1"
              data-testid="stripe-buy-button-fallback-link"
            >
              Open Stripe betaalpagina <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>
    );
  }

  // Fallback: button-styled link when admins haven't configured the embed yet.
  if (paymentLinkUrl) {
    return (
      <a
        href={paymentLinkUrl}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#4F46E5] text-white px-6 py-3 rounded-xl font-chivo font-bold uppercase text-sm tracking-wide"
        data-testid="stripe-buy-button-fallback-cta"
      >
        Betaal via Stripe <ExternalLink className="w-4 h-4" />
      </a>
    );
  }

  return (
    <div className="text-xs text-gray-500" data-testid="stripe-buy-button-missing">
      Stripe-betaalknop is nog niet geconfigureerd. Vraag de beheerder om dit veld te vullen.
    </div>
  );
}
